/**
 * Builder Domain - Data Transformations
 * Hanterar transformering av byggföretagsdata för API-anrop
 */

/**
 * Förbereder byggföretagsdata för backend (create)
 * @param {Object} values - Formulärets värden
 * @returns {Object} Data redo för backend
 */
export const prepareBuilderDataForCreate = (values) => {
  return {
    companyName: values.companyName,
    kortnamn: values.kortnamn || '',
    organizationNumber: values.organizationNumber,
    website: values.website,
    instagram: values.instagram,
    hourlyRateMin: values.hourlyRateMin,
    hourlyRateMax: values.hourlyRateMax,
    materialHandlingRate: values.materialHandlingRate,
    // Adress
    address: {
      street: values.addressStreet || '',
      zipCode: values.addressZipCode || '',
      city: values.addressCity || ''
    },
    // Försäkring
    insuranceCompany: values.insuranceCompany || '',
    // Behörigheter
    certifications: values.certifications || []
  };
};

/**
 * Förbereder byggföretagsdata för backend (update)
 * @param {Object} values - Formulärets värden
 * @returns {Object} Data redo för backend
 */
export const prepareBuilderDataForUpdate = (values) => {
  // Förbered certifications - ta bort pending files och blob URLs
  const cleanedCertifications = (values.certifications || []).map(cert => {
    const { _pendingFile, ...cleanCert } = cert;
    // Om documentUrl är en blob URL, sätt till null (den hanteras separat)
    if (cleanCert.documentUrl && cleanCert.documentUrl.startsWith('blob:')) {
      cleanCert.documentUrl = null;
    }
    return cleanCert;
  });

  return {
    companyName: values.companyName,
    kortnamn: values.kortnamn || '',
    organizationNumber: values.organizationNumber,
    website: values.website,
    instagram: values.instagram,
    contactPerson: values.contactPerson || '',
    puPriorityContact: values.puPriorityContact || '',
    additionalContactPersons: values.additionalContactPersons || [],
    hourlyRateMin: values.hourlyRateMin,
    hourlyRateMax: values.hourlyRateMax,
    materialHandlingRate: values.materialHandlingRate,
    yrkesgruppPricing: values.yrkesgruppPricing || {},
    removeAvatar: values.removeAvatar,
    // Adress
    address: {
      street: values.addressStreet || '',
      zipCode: values.addressZipCode || '',
      city: values.addressCity || ''
    },
    // Försäkring
    insuranceCompany: values.insuranceCompany || '',
    // Behörigheter
    certifications: cleanedCertifications
  };
};

/**
 * Konverterar byggföretagsdata till FormData om det finns fil eller removeAvatar
 * @param {Object} builderData - Byggföretagsdata
 * @param {boolean} isUpdate - Om det är en uppdatering
 * @param {Array} originalCertifications - Ursprungliga certifications med eventuella pending files
 * @returns {Object} { body, isFormData }
 */
export const convertToFormDataIfNeeded = (builderData, isUpdate = false, originalCertifications = []) => {
  const hasFile = builderData.avatar instanceof File;
  const hasRemoveFlag = builderData.removeAvatar === true;

  // Kolla om det finns pending certification files
  const pendingCertFiles = originalCertifications
    .map((cert, index) => ({ index, file: cert._pendingFile }))
    .filter(item => item.file instanceof File);
  const hasCertFiles = pendingCertFiles.length > 0;

  if (!hasFile && !hasRemoveFlag && !hasCertFiles) {
    return {
      body: builderData,
      isFormData: false
    };
  }

  const formData = new FormData();

  // Lägg till alla fält
  formData.append('companyName', builderData.companyName);
  formData.append('kortnamn', builderData.kortnamn || '');
  formData.append('organizationNumber', builderData.organizationNumber);
  formData.append('website', builderData.website);
  formData.append('instagram', builderData.instagram || '');
  formData.append('hourlyRateMin', builderData.hourlyRateMin);
  formData.append('hourlyRateMax', builderData.hourlyRateMax);
  formData.append('materialHandlingRate', builderData.materialHandlingRate);
  formData.append('address', JSON.stringify(builderData.address || {}));
  formData.append('insuranceCompany', builderData.insuranceCompany || '');
  formData.append('certifications', JSON.stringify(builderData.certifications || []));

  // Update-specifika fält
  if (isUpdate) {
    formData.append('contactPerson', builderData.contactPerson || '');
    formData.append('puPriorityContact', builderData.puPriorityContact || '');
    formData.append('additionalContactPersons', JSON.stringify(builderData.additionalContactPersons || []));
    formData.append('yrkesgruppPricing', JSON.stringify(builderData.yrkesgruppPricing || {}));
    formData.append('removeAvatar', builderData.removeAvatar);
  }

  // Avatar om det är en fil
  if (hasFile) {
    formData.append('avatar', builderData.avatar);
  }

  // Lägg till certification files med index
  pendingCertFiles.forEach(({ index, file }) => {
    formData.append(`certificationFile_${index}`, file);
  });

  return {
    body: formData,
    isFormData: true
  };
};

/**
 * Validerar och förbereder avatar-fil
 * @param {File} file - Avatar-filen
 * @returns {Object} { isValid, error, file }
 */
export const validateAvatarFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'Ingen fil vald', file: null };
  }
  
  if (!file.type.startsWith('image/')) {
    return { isValid: false, error: 'Endast bildfiler är tillåtna', file: null };
  }
  
  const maxSize = 1024 * 1024; // 1MB
  if (file.size > maxSize) {
    return { isValid: false, error: 'Bilden får inte vara större än 1MB', file: null };
  }
  
  return { isValid: true, error: null, file };
};

